import { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cn } from './utils';
import { Badge } from './badge';
import { Input } from './input';
import { Button } from './button';

export interface MultiSelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  value: (string | number)[];
  onChange: (value: (string | number)[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  maxDisplayed?: number;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  onSearch?: (searchTerm: string) => void;
  searchable?: boolean;
  clearable?: boolean;
  size?: 'sm' | 'default' | 'lg';
}

export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = "Select options...",
  searchPlaceholder = "Search options...",
  maxDisplayed = 3,
  disabled = false,
  loading = false,
  className,
  onSearch,
  searchable = true,
  clearable = true,
  size = "default",
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredOptions = searchTerm
    ? options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  const selectedOptions = options.filter(option => value.includes(option.value));

  const handleToggleOption = (optionValue: string | number) => {
    if (disabled) return;
    
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    
    onChange(newValue);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    onSearch?.(term);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  const displayText = () => {
    if (selectedOptions.length === 0) {
      return placeholder;
    }

    if (selectedOptions.length <= maxDisplayed) {
      return selectedOptions.map(opt => opt.label).join(', ');
    }

    return `${selectedOptions.slice(0, maxDisplayed).map(opt => opt.label).join(', ')} +${selectedOptions.length - maxDisplayed} more`;
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
  
      <Button
        type="button"
        variant="outline"
        size={size}
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || loading}
        className={cn(
          "w-full justify-between text-left font-normal",
          !value.length && "text-muted-foreground",
          isOpen && "ring-2 ring-ring"
        )}
      >
        <span className="truncate">{displayText()}</span>
        <div className="flex items-center space-x-1">
          <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
        </div>
      </Button>

      

      {isOpen && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-md">
          {searchable && (
            <div className="p-2 border-b">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground z-10" />
                <Input
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="pl-8 h-8"
                  autoFocus
                   maxLength={500}
                />
              </div>
            </div>
          )}

          <div className="max-h-40 overflow-auto p-1">
            {loading ? (
              <div className="py-2 px-3 text-sm text-muted-foreground">
                Loading...
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="py-2 px-3 text-sm text-muted-foreground">
                {searchTerm ? 'No options found' : 'No options available'}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = value.some(v =>
                  typeof v === 'string' && typeof option.value === 'string'
                    ? v.toLowerCase() === option.value.toLowerCase()
                    : v === option.value
                );
                return (
                  <div
                    key={option.value}
                    className={cn(
                      "flex items-center space-x-2 px-3 py-2 text-sm cursor-pointer rounded-lg",
                      "hover:bg-accent hover:text-accent-foreground",
                      isSelected && "bg-accent text-accent-foreground",
                      option.disabled && "opacity-50 cursor-not-allowed"
                    )}
                    onClick={() => !option.disabled && handleToggleOption(option.value)}
                  >
                    <div className={cn("h-4 w-4 border rounded-lg flex items-center justify-center", isSelected && "bg-primary border-primary")}>
                      {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                    </div>
                    <span className="flex-1">{option.label}</span>
                  </div>
                );
              })
            )}
          </div>

          {clearable && value.length > 0 && (
            <div className="border-t p-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClearAll}
                className="w-full text-xs"
              >
                Clear All ({value.length})
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
